/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { createContext } from 'react';
import type { ContextValue } from 'react-aria-components/slots';
import type { ProviderProps } from '@/lib/types';
import type { OptionsDataItem } from '../options/types';
import type { ComboBoxFieldProps } from './types';

/** React context for sharing ComboBoxField configuration across components. */
export const ComboBoxFieldContext =
  // biome-ignore lint/suspicious/noExplicitAny: Setting a type would restrict it beyond what the component allows to extend to
  createContext<ContextValue<ComboBoxFieldProps<any>, HTMLDivElement>>(null);

/**
 * Context provider for setting default props across multiple ComboBoxField components.
 *
 * @param props - The provider props.
 * @param props.children - Child components that will receive the combobox field context.
 * @returns The combobox field context provider wrapping children.
 *
 * @example
 * ```tsx
 * <ComboBoxFieldProvider size="small">
 *   <ComboBoxField defaultItems={items1}>{...}</ComboBoxField>
 *   <ComboBoxField defaultItems={items2}>{...}</ComboBoxField>
 * </ComboBoxFieldProvider>
 * ```
 */
export function ComboBoxFieldProvider<T extends OptionsDataItem>({
  children,
  ...props
}: ProviderProps<ComboBoxFieldProps<T>>) {
  return (
    <ComboBoxFieldContext.Provider value={props}>
      {children}
    </ComboBoxFieldContext.Provider>
  );
}
